import { computed, ref, watch } from 'vue';
import { projectAuth, projectFirestore } from '@/firebase/config';

/**
 * Admin-side colour theme (Settings → Theme → Modal Admin Color Settings).
 * Stored per teammate on SupportLibraries/{libId}.modalAdminThemes.{uid} —
 * the same document Theme already writes, so the save is allowed. Each key
 * maps to a CSS custom property the stylesheets read.
 */
export const MODAL_ADMIN_THEME_VARS = {
  canvasBg: ['--mi-newui-canvas-bg'],
  leftPanelBg: ['--mi-newui-left-panel-bg'],
  rightPanelBg: ['--mi-newui-right-panel-bg'],
  centerPanelBg: ['--mi-newui-center-panel-bg'],
  contentBg: ['--mi-newui-content-bg'],
  conversationPanelBg: ['--mi-newui-conversation-panel-bg'],
  textColor: ['--mi-newui-text-color', '--mi-chat-secondary-color'],
  navIconColor: ['--mi-newui-nav-icon-color'],
  primaryColor: ['--mi-chat-primary-color'],
  primaryTintDark: ['--mi-chat-primary-color-tint-dark'],
  primaryTintLight: ['--mi-chat-graydark-color-tint', '--mi-chat-primary-bg-color'],
  secondaryColor: ['--mi-chat-secondary-bg-color', '--fd-border'],
  secondaryTint: ['--mi-chat-secondary-bg-color-tint'],
  secondaryTintLight: ['--mi-chat-graylight-color-tint'],
  navExpandBg: ['--mi-newui-navx-bg'],
  navExpandBorderColor: ['--mi-newui-navx-border'],
  navExpandIconColor: ['--mi-newui-navx-icon'],
  navExpandTextColor: ['--mi-newui-navx-text'],
  navExpandHoverBg: ['--mi-newui-navx-hover-bg'],
  navExpandTextActiveColor: ['--mi-newui-navx-text-active'],
  navExpandIconActiveColor: ['--mi-newui-navx-icon-active'],
};

export const MODAL_ADMIN_THEME_DEFAULTS = Object.freeze({
  canvasBg: '#eff0eb',
  leftPanelBg: '#ffffff',
  rightPanelBg: '#ffffff',
  centerPanelBg: '#ffffff',
  contentBg: '#ffffff',
  conversationPanelBg: '#ffffff',
  textColor: '#242400',
  navIconColor: '#292914',
  primaryColor: '#0000ff',
  primaryTintDark: '#e2e4dc',
  primaryTintLight: '#edeeea',
  secondaryColor: '#edeeea',
  secondaryTint: '#fafafa',
  secondaryTintLight: '#f2f2ee',
  navExpandBg: '#eff0eb',
  navExpandBorderColor: '#e4e5df',
  navExpandIconColor: '#292914',
  navExpandTextColor: '#242400',
  navExpandHoverBg: '#ffffff',
  navExpandTextActiveColor: '#242400',
  navExpandIconActiveColor: '#0000ff',
});

/** Rendered by both the Theme settings page and the icon rail quick picker. */
export const MODAL_ADMIN_THEME_FIELDS = [
  {
    key: 'canvasBg',
    label: 'Background Color',
    short: 'Background',
    help: 'The canvas behind every panel of the admin app.',
  },
  {
    key: 'leftPanelBg',
    label: 'Left Panel Background Color',
    short: 'Left panel',
    help: 'The left navigation panel on the Inbox and on every other admin page.',
  },
  {
    key: 'centerPanelBg',
    label: 'Center Panel Inbox Background Color',
    short: 'Center panel',
    help: 'The conversation list and chat area in the Inbox.',
  },
  {
    key: 'rightPanelBg',
    label: 'Right Panel Background Color',
    short: 'Right panel',
    help: 'The Details / AI Copilot panel in the Inbox.',
  },
  {
    key: 'contentBg',
    label: 'Content Panel Background Color',
    short: 'Content Panel',
    help: 'The main content card on Settings, Reports, Articles, Form Builder and the rest.',
  },
  {
    key: 'conversationPanelBg',
    label: 'My Conversation Panel',
    short: 'Conversations',
    help: 'The My Conversations / ticket list column in the Inbox.',
  },
  {
    key: 'textColor',
    label: 'Text Color',
    short: 'Text',
    help: 'Body text, incoming chat messages, and conversation list titles.',
  },
  {
    key: 'navIconColor',
    label: 'Main Nav Icon Color',
    short: 'Nav icons',
    help: 'Idle icons in the left icon rail. Hover and active still use Primary.',
  },
  {
    key: 'primaryColor',
    label: 'Primary Color',
    short: 'Primary',
    help: 'Brand colour — active nav, agent bubbles, links and highlights.',
  },
  {
    key: 'primaryTintDark',
    label: 'Primary Tint Dark Color',
    short: 'Primary dark',
    help: 'Darker primary wash used on hover and selected conversation rows.',
  },
  {
    key: 'primaryTintLight',
    label: 'Primary Tint Light Color',
    short: 'Primary light',
    help: 'Light wash used on subtle highlights (--mi-chat-graydark-color-tint).',
  },
  {
    key: 'secondaryColor',
    label: 'Secondary Color',
    short: 'Secondary',
    help: 'Secondary surface colour used across the admin chrome.',
  },
  {
    key: 'secondaryTint',
    label: 'Secondary Tint Color',
    short: 'Secondary tint',
    help: 'Tinted secondary wash for muted surfaces.',
  },
  {
    key: 'secondaryTintLight',
    label: 'Secondary Tint Light Color',
    short: 'Secondary light',
    help: 'Lightest secondary wash for hairline fills and list hovers.',
  },
  {
    key: 'navExpandBg',
    group: 'Customize Expanded Menu',
    label: 'Expanded Menu Background',
    short: 'Background',
    help: 'The card behind the expanded (hover) main navigation.',
  },
  {
    key: 'navExpandBorderColor',
    group: 'Customize Expanded Menu',
    label: 'Expanded Menu Border Color',
    short: 'Border',
    help: 'Separator lines inside the expanded navigation — under the header, above each group and above Settings.',
  },
  {
    key: 'navExpandIconColor',
    group: 'Customize Expanded Menu',
    label: 'Expanded Menu Icon Color',
    short: 'Icon',
    help: 'Idle icons and group headings inside the expanded navigation.',
  },
  {
    key: 'navExpandTextColor',
    group: 'Customize Expanded Menu',
    label: 'Expanded Menu Link Text Color',
    short: 'Link text',
    help: 'Menu names and the Modal wordmark in the expanded navigation.',
  },
  {
    key: 'navExpandHoverBg',
    group: 'Customize Expanded Menu',
    label: 'Expanded Menu Hover Link Background',
    short: 'Hover bg',
    help: 'Rounded highlight behind a hovered or active menu link.',
  },
  {
    key: 'navExpandTextActiveColor',
    group: 'Customize Expanded Menu',
    label: 'Expanded Menu Link Hover / Active Text Color',
    short: 'Link hover text',
    help: 'Menu name colour once a link is hovered or is the active route.',
  },
  {
    key: 'navExpandIconActiveColor',
    group: 'Customize Expanded Menu',
    label: 'Expanded Menu Icon Hover / Active Color',
    short: 'Icon hover',
    help: 'Icon colour once a link is hovered or is the active route.',
  },
];

/**
 * Same fields bucketed by their optional `group`, in declaration order, so the
 * picker can print a heading above a run of related inputs. Ungrouped fields
 * keep an empty name and render with no heading.
 */
export const MODAL_ADMIN_THEME_FIELD_GROUPS = MODAL_ADMIN_THEME_FIELDS.reduce((groups, field) => {
  const name = field.group || '';
  const current = groups[groups.length - 1];
  if (current && current.name === name) current.fields.push(field);
  else groups.push({ name, fields: [field] });
  return groups;
}, []);

/** Curated preset palettes — each is a full theme object. */
export const MODAL_ADMIN_THEME_PRESETS = [
  {
    id: 'olive',
    name: 'Olive Earth',
    colors: {
      canvasBg: '#e2e4dc',
      leftPanelBg: '#f3f4f0',
      rightPanelBg: '#ffffff',
      centerPanelBg: '#ffffff',
      contentBg: '#ffffff',
      conversationPanelBg: '#ffffff',
      textColor: '#242400',
      navIconColor: '#292914',
      primaryColor: '#0000ff',
      primaryTintDark: '#e2e4dc',
      primaryTintLight: '#edeeea',
      secondaryColor: '#edeeea',
      secondaryTint: '#fafafa',
      secondaryTintLight: '#f2f2ee',
      navExpandBg: '#e2e4dc',
      navExpandBorderColor: '#d6d8ce',
      navExpandIconColor: '#292914',
      navExpandTextColor: '#242400',
      navExpandHoverBg: '#edeeea',
      navExpandTextActiveColor: '#242400',
      navExpandIconActiveColor: '#0000ff',
    },
  },
  {
    id: 'azure',
    name: 'Azure Breeze',
    colors: {
      canvasBg: '#dfefff',
      leftPanelBg: '#fdfeff',
      rightPanelBg: '#ffffff',
      centerPanelBg: '#ffffff',
      contentBg: '#ffffff',
      conversationPanelBg: '#ffffff',
      textColor: '#1a2a3a',
      navIconColor: '#2c3e50',
      primaryColor: '#2980b9',
      primaryTintDark: '#c8dff0',
      primaryTintLight: '#e8f2fb',
      secondaryColor: '#e4eef7',
      secondaryTint: '#f5f9fd',
      secondaryTintLight: '#f0f6fc',
      navExpandBg: '#dfefff',
      navExpandBorderColor: '#cfe3f5',
      navExpandIconColor: '#2c3e50',
      navExpandTextColor: '#1a2a3a',
      navExpandHoverBg: '#ffffff',
      navExpandTextActiveColor: '#12354f',
      navExpandIconActiveColor: '#2980b9',
    },
  },
  {
    id: 'gold',
    name: 'Golden Glow',
    colors: {
      canvasBg: '#eae4d0',
      leftPanelBg: '#faf7f0',
      rightPanelBg: '#ffffff',
      centerPanelBg: '#ffffff',
      contentBg: '#ffffff',
      conversationPanelBg: '#ffffff',
      textColor: '#2d2006',
      navIconColor: '#3d2e12',
      primaryColor: '#b8860b',
      primaryTintDark: '#ece0c8',
      primaryTintLight: '#f3eee0',
      secondaryColor: '#f0e8d8',
      secondaryTint: '#faf7f0',
      secondaryTintLight: '#f7f3eb',
      navExpandBg: '#eae4d0',
      navExpandBorderColor: '#ded6bd',
      navExpandIconColor: '#3d2e12',
      navExpandTextColor: '#2d2006',
      navExpandHoverBg: '#f3eee0',
      navExpandTextActiveColor: '#6b4e0a',
      navExpandIconActiveColor: '#b8860b',
    },
  },
  {
    id: 'slate',
    name: 'Tech Slate',
    colors: {
      canvasBg: '#e8eaee',
      leftPanelBg: '#f2f3f5',
      rightPanelBg: '#ffffff',
      centerPanelBg: '#ffffff',
      contentBg: '#ffffff',
      conversationPanelBg: '#ffffff',
      textColor: '#1e1e2e',
      navIconColor: '#2e2e3e',
      primaryColor: '#6c5ce7',
      primaryTintDark: '#d5d1e8',
      primaryTintLight: '#e8e5f2',
      secondaryColor: '#e4e3ea',
      secondaryTint: '#f5f4f8',
      secondaryTintLight: '#efeff4',
      navExpandBg: '#e8eaee',
      navExpandBorderColor: '#dcdee4',
      navExpandIconColor: '#2e2e3e',
      navExpandTextColor: '#1e1e2e',
      navExpandHoverBg: '#ffffff',
      navExpandTextActiveColor: '#4b3fbf',
      navExpandIconActiveColor: '#6c5ce7',
    },
  },
  {
    id: 'frost',
    name: 'Arctic Frost',
    colors: {
      canvasBg: '#eef1f5',
      leftPanelBg: '#f7f8fa',
      rightPanelBg: '#ffffff',
      centerPanelBg: '#ffffff',
      contentBg: '#ffffff',
      conversationPanelBg: '#ffffff',
      textColor: '#1c1f26',
      navIconColor: '#3a3f4b',
      primaryColor: '#0066ff',
      primaryTintDark: '#d4e2f4',
      primaryTintLight: '#e9eff8',
      secondaryColor: '#e5e9ef',
      secondaryTint: '#f3f5f8',
      secondaryTintLight: '#f0f2f6',
      navExpandBg: '#eef1f5',
      navExpandBorderColor: '#dfe4ea',
      navExpandIconColor: '#3a3f4b',
      navExpandTextColor: '#1c1f26',
      navExpandHoverBg: '#ffffff',
      navExpandTextActiveColor: '#0052cc',
      navExpandIconActiveColor: '#0066ff',
    },
  },
  {
    id: 'blush',
    name: 'Soft Blush',
    colors: {
      canvasBg: '#f2eded',
      leftPanelBg: '#f9f5f5',
      rightPanelBg: '#ffffff',
      centerPanelBg: '#ffffff',
      contentBg: '#ffffff',
      conversationPanelBg: '#ffffff',
      textColor: '#2a1f1f',
      navIconColor: '#4a3636',
      primaryColor: '#d4537e',
      primaryTintDark: '#ebd5de',
      primaryTintLight: '#f3e8ed',
      secondaryColor: '#ece4e7',
      secondaryTint: '#f8f3f5',
      secondaryTintLight: '#f5f0f2',
      navExpandBg: '#f2eded',
      navExpandBorderColor: '#e6dcdc',
      navExpandIconColor: '#4a3636',
      navExpandTextColor: '#2a1f1f',
      navExpandHoverBg: '#ffffff',
      navExpandTextActiveColor: '#a83a60',
      navExpandIconActiveColor: '#d4537e',
    },
  },
];

export function getDefaultModalAdminTheme() {
  return { ...MODAL_ADMIN_THEME_DEFAULTS };
}

/** Fills in any key the stored document is missing, and drops unknown ones. */
export function normalizeModalAdminTheme(theme) {
  const normalized = getDefaultModalAdminTheme();
  if (!theme) return normalized;
  Object.keys(MODAL_ADMIN_THEME_DEFAULTS).forEach(key => {
    if (typeof theme[key] === 'string' && theme[key].trim()) {
      normalized[key] = theme[key].trim();
    }
  });
  return normalized;
}

/**
 * Writes the theme onto :root. The stylesheets declare some of these with
 * !important, so the inline values need the same priority to win.
 */
export function applyModalAdminTheme(theme) {
  const resolved = normalizeModalAdminTheme(theme);
  const root = document.documentElement;
  Object.entries(MODAL_ADMIN_THEME_VARS).forEach(([key, cssVars]) => {
    cssVars.forEach(cssVar => root.style.setProperty(cssVar, resolved[key], 'important'));
  });
  return resolved;
}

export function clearModalAdminTheme() {
  const root = document.documentElement;
  Object.values(MODAL_ADMIN_THEME_VARS).forEach(cssVars => {
    cssVars.forEach(cssVar => root.style.removeProperty(cssVar));
  });
}

/**
 * The quick colour picker and the admin colour palette are an internal tool,
 * so only these accounts get the Settings switch or the rail icon. Gating the
 * saved flag as well means a stored `shortcut: true` cannot surface the icon
 * for anyone else.
 */
export const MODAL_ADMIN_THEME_ALLOWED_UIDS = Object.freeze([
  'O8laZfstvuYPsJtg1Iqda3PjtDr1',
  'BGStCVmDAcUQ3saeMCDnvYL4szg2',
]);

function getThemeOwnerId() {
  return projectAuth.currentUser?.uid || localStorage.getItem('user_id') || '';
}

function getSupportLibId() {
  return localStorage.getItem('support_Lib_id') || '';
}

function supportLibraryDocRef(libId = getSupportLibId()) {
  return projectFirestore.collection('SupportLibraries').doc(libId);
}

function ownerThemeField(uid) {
  return `modalAdminThemes.${uid}`;
}

function readOwnerTheme(data, uid) {
  if (!data || !uid) return null;
  return data.modalAdminThemes?.[uid] || null;
}

/* ---- localStorage helpers (reliable per-user fallback) ---- */
function localStorageThemeKey(uid) {
  return `modalAdminTheme_${uid}`;
}

function saveToLocalStorage(uid, record) {
  try {
    localStorage.setItem(localStorageThemeKey(uid), JSON.stringify(record));
  } catch (_) { /* storage full or disabled */ }
}

function loadFromLocalStorage(uid) {
  try {
    const raw = localStorage.getItem(localStorageThemeKey(uid));
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

/**
 * Firestore is best-effort, so the two copies can disagree: a teammate without
 * write access on the library document still saves locally. Trust whichever
 * copy was stamped last, otherwise the snapshot listener would roll the user's
 * own change straight back after a rejected sync.
 */
function pickLatestTheme(remote, local) {
  if (!remote) return local || null;
  if (!local) return remote;
  return Number(local.updatedAt || 0) > Number(remote.updatedAt || 0) ? local : remote;
}

/**
 * Saves theme data. Always writes to localStorage (works for every user).
 * Attempts Firestore as best-effort sync — if the user lacks write permission
 * on the SupportLibraries document the error is silently caught.
 */
async function writeOwnerTheme(record) {
  const uid = getThemeOwnerId();
  const libId = getSupportLibId();
  if (!uid) return;

  const stamped = { ...record, updatedAt: Date.now() };
  saveToLocalStorage(uid, stamped);

  if (!libId) return;
  try {
    await supportLibraryDocRef(libId).update({
      [ownerThemeField(uid)]: stamped,
    });
  } catch (err) {
    console.log('Firestore write skipped for admin theme (permission or network):', err?.code || err?.message);
  }
}

/*
 * One theme per signed-in admin, so the state lives at module scope: the Theme
 * settings page and the icon rail quick picker edit the very same object.
 */
const theme = ref(getDefaultModalAdminTheme());
const savedTheme = ref(getDefaultModalAdminTheme());
const isShortcutEnabled = ref(false);
const savedShortcutEnabled = ref(false);
const isNavExpandEnabled = ref(false);
const savedNavExpandEnabled = ref(false);
const isSaving = ref(false);

// Tracked as a ref rather than read on demand, because the shell mounts before
// Firebase has restored the session — `subscribeModalAdminTheme` refreshes it.
const ownerId = ref(getThemeOwnerId());
const canUseAdminTheme = computed(() =>
  MODAL_ADMIN_THEME_ALLOWED_UIDS.includes(ownerId.value),
);
const isShortcutActive = computed(
  () => canUseAdminTheme.value && savedShortcutEnabled.value,
);

const isDirty = computed(() =>
  Object.keys(MODAL_ADMIN_THEME_DEFAULTS).some(key => theme.value[key] !== savedTheme.value[key]),
);

const areTogglesDirty = computed(
  () =>
    isShortcutEnabled.value !== savedShortcutEnabled.value ||
    isNavExpandEnabled.value !== savedNavExpandEnabled.value,
);

// Sync so that signing out can wipe the inline values right after resetting the
// refs, instead of the flushed watcher re-writing them onto :root afterwards.
watch(theme, value => applyModalAdminTheme(value), { deep: true, flush: 'sync' });

export function resetModalAdminTheme() {
  theme.value = getDefaultModalAdminTheme();
}

/**
 * Both switches are drafts until saved: the rail palette icon and the expanded
 * nav follow the *saved* flags, so flipping a switch on the Theme page previews
 * nothing until Save changes is pressed.
 */
export async function saveModalAdminToggles() {
  isSaving.value = true;
  try {
    await writeOwnerTheme({
      ...savedTheme.value,
      shortcut: isShortcutEnabled.value,
      navExpand: isNavExpandEnabled.value,
    });
    savedShortcutEnabled.value = isShortcutEnabled.value;
    savedNavExpandEnabled.value = isNavExpandEnabled.value;
  } finally {
    isSaving.value = false;
  }
}

/** Colours only — carries the saved switches through so a rail-picker save
    never commits a switch the user is still deciding on. */
export async function saveModalAdminTheme() {
  isSaving.value = true;
  try {
    await writeOwnerTheme({
      ...theme.value,
      shortcut: savedShortcutEnabled.value,
      navExpand: savedNavExpandEnabled.value,
    });
    savedTheme.value = { ...theme.value };
  } finally {
    isSaving.value = false;
  }
}

/**
 * Applies the signed-in user's theme and keeps every open tab in sync. Follows
 * auth rather than taking a uid, because the shell mounts before Firebase has
 * necessarily restored the session. Returns the teardown function.
 */
export function subscribeModalAdminTheme() {
  let unsubscribeDoc = null;

  const stopWatchingDoc = () => {
    if (unsubscribeDoc) {
      unsubscribeDoc();
      unsubscribeDoc = null;
    }
  };

  const applyStored = (stored, force) => {
    const hadUnsavedEdits = isDirty.value;
    const shortcutPending = isShortcutEnabled.value !== savedShortcutEnabled.value;
    const navExpandPending = isNavExpandEnabled.value !== savedNavExpandEnabled.value;

    // Accounts outside the allow-list always sit on the default palette, even
    // if they saved colours before the picker was restricted. The expanded nav
    // switch is unrestricted, so that one is still read back for everyone.
    const colours = canUseAdminTheme.value ? stored : null;

    savedTheme.value = normalizeModalAdminTheme(colours);
    savedShortcutEnabled.value = canUseAdminTheme.value && stored?.shortcut === true;
    savedNavExpandEnabled.value = stored?.navExpand === true;

    if (force || !hadUnsavedEdits) {
      theme.value = { ...savedTheme.value };
    }
    if (force || !shortcutPending) {
      isShortcutEnabled.value = savedShortcutEnabled.value;
    }
    if (force || !navExpandPending) {
      isNavExpandEnabled.value = savedNavExpandEnabled.value;
    }
  };

  const attachLibraryListener = uid => {
    stopWatchingDoc();
    ownerId.value = uid;
    const libId = getSupportLibId();
    if (!uid || !libId) {
      theme.value = getDefaultModalAdminTheme();
      savedTheme.value = getDefaultModalAdminTheme();
      isShortcutEnabled.value = false;
      savedShortcutEnabled.value = false;
      isNavExpandEnabled.value = false;
      savedNavExpandEnabled.value = false;
      clearModalAdminTheme();
      return;
    }

    // Eager load from localStorage so the UI reflects the user's settings
    // immediately, before the Firestore snapshot arrives.
    const local = loadFromLocalStorage(uid);
    if (local) {
      applyStored(local, true);
    }

    unsubscribeDoc = supportLibraryDocRef(libId).onSnapshot(
      snap => {
        const remote = readOwnerTheme(snap.exists ? snap.data() : null, uid);
        applyStored(pickLatestTheme(remote, loadFromLocalStorage(uid)), false);
      },
      err => console.log('Error while loading admin theme colors', err),
    );
  };

  const unsubscribeAuth = projectAuth.onAuthStateChanged(user => {
    attachLibraryListener(user?.uid || localStorage.getItem('user_id') || '');
  });

  return () => {
    stopWatchingDoc();
    unsubscribeAuth();
  };
}

export function useModalAdminTheme() {
  return {
    modalAdminTheme: theme,
    savedModalAdminTheme: savedTheme,
    modalAdminThemeDefaults: MODAL_ADMIN_THEME_DEFAULTS,
    modalAdminThemePresets: MODAL_ADMIN_THEME_PRESETS,
    modalAdminThemeFields: MODAL_ADMIN_THEME_FIELDS,
    modalAdminThemeFieldGroups: MODAL_ADMIN_THEME_FIELD_GROUPS,
    // Draft values — bind these to the Settings switches.
    isModalAdminThemeShortcutEnabled: isShortcutEnabled,
    isModalAdminNavExpandEnabled: isNavExpandEnabled,
    // Whether this account may see the colour picker at all.
    canUseModalAdminTheme: canUseAdminTheme,
    // Saved values — everything that reacts to the switches reads these.
    isModalAdminThemeShortcutActive: isShortcutActive,
    isModalAdminNavExpandActive: savedNavExpandEnabled,
    isModalAdminThemeDirty: isDirty,
    areModalAdminTogglesDirty: areTogglesDirty,
    isSavingModalAdminTheme: isSaving,
    saveModalAdminTheme,
    saveModalAdminToggles,
    resetModalAdminTheme,
  };
}
